import React, { useState, useEffect, useContext } from 'react';
import axiosConfig from '../../../utils/axiosConfig';
import { useImmer } from 'use-immer';
import MUIDataTable from 'mui-datatables';
import { StateContext, DispatchContext } from '../../../context/Provider';
import { IconButton, Tooltip } from '@material-ui/core';

import { 
    BackspaceTwoTone as BackspaceTwoToneIcon,
    Delete as DeleteIcon,
    DeleteForeverTwoTone as DeleteForeverTwoToneIcon,
    Edit as EditIcon,
    VerifiedUserTwoTone as VerifiedUserTwoToneIcon,
    RotateLeft as RotateLeftIcon
} from '@material-ui/icons';

import ResponsiveLayout from '../../Layouts/ResponsiveLayout';
import BackdropLoader from '../../../components/Shared/BackdropLoader/BackdropLoader';
import UserModalForm from './Modals/UserModalForm';
import Toolbar from './Toolbar/Toolbar';

const Users = (props) => {

    const appState = useContext(StateContext);
    const appDispatch = useContext(DispatchContext);
    
    const { authState, globalState } = appState;
    const { globalDispatch } = appDispatch;

    const [open, setOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [showSchoolColumn, setShowSchoolColumn] = useState(authState.user.school_id === 0 || false);

    const handleClick = (tableMeta) => {

        setSelectedUser({
            id: tableMeta.rowData[0],
            avatar: tableMeta.rowData[1],
            role: tableMeta.rowData[2],
            name: tableMeta.rowData[3],
            email: tableMeta.rowData[4],
            school_id: tableMeta.rowData[5],
            role_id: tableMeta.rowData[6],
        });

        setOpen(true);
    }

    const handleClose = () => {
        setOpen(false);
    }

    const [options, setOptions] = useState({
        filter: true,
        filterType: 'dropdown',
        responsive: 'simple',
        rowsPerPage: 10,
        selectableRows: false, // turn off checkbox in rows
        customToolbar: () => {
            return (<Toolbar fetchUsers={fetchUsers} />);
        }
    });

    const [columns, setColumns] = useState([
        { label: "#", name: "id", options: { display: true } },
        { label: "Avatar", name: "avatar",
            options: { 
                customBodyRender: (value, tableMeta, updateValue) => {
                    return (
                        <img 
                            src={value}
                            width="50px"
                            height="50px"
                        />
                    );
                }
            }
        },
        { label: "Role", name: "role" },
        { label: "Full Name", name: "name" },
        { label: "Email", name: "email" },
        { label: "School ID", name: "school_id", options: { display: false, viewColumns: false, filter: false } },
        { label: "Role ID", name: "role_id", options: { display: false, viewColumns: false, filter: false } },
        { label: "School", name: "school_name", options: { display: showSchoolColumn, viewColumns: showSchoolColumn, filter: showSchoolColumn } },
        { label: "Status", name: "is_active" },
        { label: "Created At", name: "created_at" },
        { label: 'Actions', name: "actions",
            options: { 
                viewColumns: false,
                filter: false,
                customBodyRender: (value, tableMeta, updateValue) => {

                    let currentStatus = tableMeta.rowData[8];
                    let msg = 'Deactivate';

                    let forceResetIcon = (
                        <Tooltip title="Force reset password">
                            <RotateLeftIcon fontSize="small" />
                        </Tooltip>
                    );

                    let actionIcon = (
                        <Tooltip title="Deactivate">
                            <BackspaceTwoToneIcon fontSize="small" />
                        </Tooltip>
                    );

                    if (currentStatus === 'Inactive') {
                        msg = 'Activate';
                        actionIcon = (
                            <Tooltip title="Activate">
                                <VerifiedUserTwoToneIcon fontSize="small" />
                            </Tooltip>
                        );
                    }

                    return (
                        <>
                            <IconButton 
                                aria-label="edit" 
                                onClick={() => handleClick(tableMeta)}>
                                <Tooltip title="Edit">
                                    <EditIcon fontSize="small" />
                                </Tooltip>
                            </IconButton>

                            { authState.user.id !== tableMeta.rowData[0] && (
                                <IconButton 
                                    aria-label="delete" 
                                    onClick={() => { 
                                        window.confirm(`Force reset password?`) && forceResetPassword(tableMeta) 
                                    }}>
                                    {forceResetIcon}
                                </IconButton>
                            )}

                            { tableMeta.rowData[6] !== 1 && authState.user.id !== tableMeta.rowData[0] && (
                                <>
                                    <IconButton 
                                        aria-label="delete" 
                                        onClick={() => { 
                                            window.confirm(`Are you sure you want to ${msg} this item?`) && removeItem(tableMeta) 
                                        }}>
                                        {actionIcon}
                                    </IconButton>
                                    { authState.user.role_id === 1 && (
                                        <IconButton 
                                            aria-label="delete" 
                                            onClick={() => { 
                                                window.confirm(`Are you sure you want to Force Delete this user?`) && forceDelete(tableMeta) 
                                            }}>
                                            <Tooltip title="Force Delete">
                                                <DeleteForeverTwoToneIcon fontSize="default" />
                                            </Tooltip>
                                        </IconButton>
                                    )}
                                </>
                            )}
                        </>
                    )
                }
            }
        }
    ]);

    const [state, setState] = useImmer({
        isLoading: false,
        users: []
    });

    let loadingState = false;
    const [isLoading, setIsLoading] = useState(false);

    async function fetchUsers() {

        try {
            const response = await axiosConfig.get(`/users`); 
            const { data } = response.data;

            // console.log(data);
            const usersArr = [];
            data.map(function (data, index) {
                data.is_active = data.is_active == 1 ? 'Active' : 'Inactive'; 
                usersArr.push(data);
            });

            setState(draft => {
                draft.users = usersArr;
            });

        } catch (e) {
            console.log(e);
        }

        globalDispatch({ type: "setIsLoading", value: false});
    }

    async function removeItem(tableMeta) {
        const userID = tableMeta.rowData[0];
        // console.log(userID);

        try {
            const response = await axiosConfig.post(`/users/${userID}`, {
                _method: "DELETE"
            }); 

            const { status_code, message } = response.data;

            if (status_code == 'ok') {
                // console.log(message);
                fetchUsers();
            }

        } catch (e) {
            console.log(e);
        }
    }

    async function forceDelete(tableMeta) {
        const userID = tableMeta.rowData[0];
        try {
            const response = await axiosConfig.post(`/forceDeleteUser`, {
                user_id: userID
            });

            const { status_code, message } = response.data;

            if (status_code === 'ok') {
                globalDispatch({type: 'setSnackbarMsg', value: 'User deleted successfully.'});
                globalDispatch({type: 'setIsOpenSnackbar', value: true});
                fetchUsers();
            }

            globalDispatch({type: 'setIsLoading', value: false});
            setTimeout(() => {
                globalDispatch({type: 'setIsOpenSnackbar', value: false});
            }, 1200);

        } catch (e) {
            console.log(e);
        }
    }

    async function forceResetPassword(tableMeta) {
        const userID = tableMeta.rowData[0];
        try {
            const response = await axiosConfig.post(`/forceResetPassword`, {
                user_id: userID
            });

            const { status_code, message } = response.data;

            if (status_code === 'ok') {
                globalDispatch({type: 'setSnackbarMsg', value: 'Password has been reset successfully.'});
                globalDispatch({type: 'setIsOpenSnackbar', value: true});
                fetchUsers();
            }

            globalDispatch({type: 'setIsLoading', value: false});
            setTimeout(() => {
                globalDispatch({type: 'setIsOpenSnackbar', value: false});
            }, 1200);

        } catch (e) {
            console.log(e);
        }
    }

    useEffect(() => {

        // console.clear();
        globalDispatch({ type: "setIsLoading", value: true});
        const source = axiosConfig.CancelToken.source();

        fetchUsers();

        return () => {
            source.cancel();
        }

    }, []);

    return (
        <ResponsiveLayout>
            <UserModalForm
                dialogTitle={'Edit User'}
                open={open}
                handleClose={handleClose}
                fetchUsers={fetchUsers}
                selectedUser={selectedUser}
            />

            { state.isLoading 
                ? null
                : <MUIDataTable 
                    title={"List of Users"} 
                    data={state.users} 
                    columns={columns} 
                    options={options} 
                />
            }
        </ResponsiveLayout>
    );
}

export default Users;